import { Moment } from "moment";
import { OfficeEntity } from "./AdvanceHotDeskingService";
import { CapacityResponse } from "./OfficeCapacityService";
import { agent } from "@shared/services/agent";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { Parser } from "json2csv";
import { saveAs } from "file-saver";
import { WhereaboutsOption } from "./WhereaboutOptions";
import { SimpleUserWithResponsibilitiesAndLocation, UnknownWhereaboutsUserInfo } from '@shared/models/user.models';
import { DateScale } from '@shared/ui/molecules/DateFlicker';
import { TeamEntity } from '@shared/models/team.models';
import { Tag } from '@shared/models/tags.models';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';

export function getMonthlyOfficeUsage(date: Moment, office: OfficeEntity, toDate?: Moment, team?: TeamEntity, workGroup?: Tag, department?: Department): Promise<CapacityResponse[]> {
  if (!toDate) {
    date = date.clone().startOf('month');
    toDate = date.clone().endOf('month');
  }
  let qs = '';
  if (team) {
    qs += `&teamId=${team.id}`;
  } else if (workGroup) {
    qs += `&workGroupId=${workGroup.id}`;
  } else if (department) {
    qs += `&departmentId=${department.id}`;
  }
  return agent.requests.get(`/api/report/office/usage/month?office=${office.id}&date=${date.format(DATE_FORMAT)}&toDate=${toDate.format(DATE_FORMAT)}${qs}`);
}

export function getWhereaboutsSummary(date: Moment, teamId: string): Promise<WhereaboutsSummaryReport> {
  const from = date.clone().startOf('month').format(DATE_FORMAT);
  const to = date.clone().endOf('month').format(DATE_FORMAT);
  return agent.requests.get(`/api/report/whereabouts/summary?teamId=${teamId}&from=${from}&to=${to}`, true);
}

export function getDepartmentsWhereaboutsSummary(date: Moment, departmentId: number): Promise<WhereaboutsSummaryReport> {
  const from = date.clone().startOf('month').format(DATE_FORMAT);
  const to = date.clone().endOf('month').format(DATE_FORMAT);
  return agent.requests.get(`/api/report/whereabouts/summary?departmentId=${departmentId}&from=${from}&to=${to}`, true);
}

export function getAllDepartmentsWhereaboutsSummary(date: Moment): Promise<WhereaboutsSummaryReport> {
  const from = date.clone().startOf('month').format(DATE_FORMAT);
  const to = date.clone().endOf('month').format(DATE_FORMAT);
  return agent.requests.get(`/api/report/whereabouts/summary?allDepartments=true&from=${from}&to=${to}`, true);
}

export function getWorkgroupsWhereaboutsSummary(date: Moment, workGroupId: number): Promise<WhereaboutsSummaryReport> {
  const from = date.clone().startOf('month').format(DATE_FORMAT);
  const to = date.clone().endOf('month').format(DATE_FORMAT);
  return agent.requests.get(`/api/report/whereabouts/summary?workGroupId=${workGroupId}&from=${from}&to=${to}`, true);
}

export function getAllWorkGroupsWhereaboutsSummary(date: Moment): Promise<WhereaboutsSummaryReport> {
  const from = date.clone().startOf('month').format(DATE_FORMAT);
  const to = date.clone().endOf('month').format(DATE_FORMAT);
  return agent.requests.get(`/api/report/whereabouts/summary?allWorkGroups=true&from=${from}&to=${to}`, true);
}

export interface WhereaboutsSummaryReport {
  whereaboutsOptionList: WhereaboutsOption[];
  whereaboutsUsage: any[];
}

function getOfficeUsageReport(office: OfficeEntity, from: Moment, to: Moment, teamId?: string, workGroupId?: number): Promise<any> {
  let queryString = `?from=${from.format(DATE_FORMAT)}&to=${to.format(DATE_FORMAT)}&office=${office.id}`
  if (teamId) {
    queryString += `&team=${teamId}`
  }
  if (workGroupId) {
    queryString += `&workgroup=${workGroupId}`
  }
  return agent.requests.get(`/api/report/office/usage/dates${queryString}`)
}

export interface OfficeOccupancyUsage {
  officeId: number;
  name: string;
  capacity: number;
  averageUsage: number;
  usage: number;
  daysInRange: number;
}

export interface DeskUsage {
  deskId: number;
  label: string;
  usage: number;
  daysInRange: number;
}

export function getOfficeOccupancyUsage(office: OfficeEntity, from: Moment, to?: Moment): Promise<OfficeOccupancyUsage[]>{
  const fromDate = !!to ? from : from.clone().startOf('month');
  const toDate = !!to ? to : fromDate.clone().add(1, 'month');

  return agent.requests.get(`/api/report/office-occupancy?officeId=${office.id}&fromDate=${fromDate.format(DATE_FORMAT)}&toDate=${toDate.format(DATE_FORMAT)}`)
}

export function getOfficeDeskUsage(office: OfficeEntity, from: Moment, to?: Moment, teamId?: string, workGroupId?: number, departmentId?: number): Promise<DeskUsage[]>{
  const fromDate = !!to ? from : from.clone().startOf('month');
  const toDate = !!to ? to : fromDate.clone().add(1, 'month');

  let queryString = `officeId=${office.id}&fromDate=${fromDate.format(DATE_FORMAT)}&toDate=${toDate.format(DATE_FORMAT)}`;

  if (teamId) {
    queryString += `&teamId=${teamId}`;
  } else if (workGroupId) {
    queryString += `&workGroupId=${workGroupId}`;
  } else if (departmentId) {
    queryString += `&departmentId=${departmentId}`;
  }

  return agent.requests.get(`/api/report/desk/usage?${queryString}`)
}

export function getUnknownWhereaboutsReport(date: Moment): Promise<UnknownWhereaboutsUserInfo[]> {
  return agent.requests.get(`/api/report/unknown-whereabouts?date=${date.format(DATE_FORMAT)}`)
}

export function getWhereaboutsReport(companyId: string, from: Moment, to: Moment, userId?: string, teamId?: string, departmentId?: number, workGroupId?: number, hideUnknownWhereabouts?: boolean, allDepartments?: boolean, allWorkGroups?: boolean): Promise<any> {
  return agent.requests.post('/api/report/whereabouts', {
    companyId: companyId,
    userId: userId,
    fromDate: from?.format(DATE_FORMAT),
    toDate: to?.format(DATE_FORMAT),
    teamId: teamId,
    departmentId: departmentId,
    workGroupId: workGroupId,
    hideUnknownWhereabouts: hideUnknownWhereabouts,
    allDepartments: allDepartments,
    allWorkGroups: allWorkGroups,
  });
}

export async function downloadDetailedHolidayReport(year: number): Promise<HolidayReportLine[]> {
  return await agent.requests.get(`/api/report/holiday-report?year=${year}`);
}

export async function downloadAbsenceReport(from: Moment, to: Moment): Promise<AbsenceReportLine[]> {
  return await agent.requests.get(`/api/report/absence?from=${from.format(DATE_FORMAT)}&to=${to.format(DATE_FORMAT)}`);
}

export interface HolidayReportLine {
  userId: string;
  firstName: string;
  lastName: string;
  teamName: string;
  startDate: string;
  endDate: string;
  status: string;
  daysTaken: number;
  totalDaysTaken: number;
  remaining: number;
  allowance: number;
}

export interface AbsenceReportLine {
  userId: string;
  firstName: string;
  lastName: string;
  whereaboutsStatus: string;
  date: string;
  duration: string;
  count: number;
}

export function getCheckInSummary(office: OfficeEntity, date: Moment): Promise<any> {
  return agent.requests.get(`/api/report/check-in/summary?officeId=${office.id}&date=${date.format(DATE_FORMAT)}`);
}

export interface AutomaticCheckinLog {
  id: number;
  userId: string;
  networkName: string;
  macAddress: string;
  dateTime: string;
}

export function getAutomaticCheckins(date: Moment): Promise<AutomaticCheckinLog[]> {
  return agent.requests.get(`/api/report/automatic-checkin?date=${date.format(DATE_FORMAT)}`);
}

export function getAllLineReports(): Promise<LineReportLine[]> {
  return agent.requests.get(`/api/report/line-reports`)
}

export interface UsersSingleWhereaboutsBreakdown {
  key: string;
  count: number;
  locationId: number;
  label: string;
  icon: string;
  fill?: string;
  whereaboutsOption?: WhereaboutsOption;
}

export function getUserWhereaboutsBreakdown(userId: string, date: Moment, scaleDate: DateScale): Promise<UsersSingleWhereaboutsBreakdown[]> {
  let qs = ``;
  qs += `userId=${userId}`;
  if (scaleDate === DateScale.Month) {
    qs += `&year=${date.year()}&month=${date.month() + 1}`;
  } else if (scaleDate === DateScale.Week) {
    qs += `&year=${date.year()}&month=${date.month() + 1}&day=${date.date()}`;
  } else {
    qs += `&year=${date.year()}`;
  }
  return agent.requests.get(`/api/report/user?${qs}`, true, true);
}

export function getWeeklyOfficeUsage(office: OfficeEntity, date: Moment): Promise<SimpleUserWithResponsibilitiesAndLocation[]> {
  return agent.requests.get(`/api/report/office/usage/average?office=${office.id}&date=${date.format(DATE_FORMAT)}`);
}

export function toCsvDownload(contents: any[], fileName: string, fields?: string[]) {
  const json2csvParser = new Parser({quote: '', fields, withBOM: true, excelStrings: true});
  const csv = json2csvParser.parse(contents);

  const blob = new Blob([csv], {type: "text/csv;charset=utf-8"});
  saveAs(blob, `Team Today ${fileName}.csv`);
}

export function toExcelDownload(contents: any[], fileName: string, fields?: string[]) {
  toCsvDownload(contents, fileName, fields);
  // const opts = {
  //   quote: '',
  //   fields,
  //   withBOM: true,
  //   excelStrings: true,
  //   formatters: stringExcel
  // }
  // const json2csvParser = new Parser(opts);
  // const csv = json2csvParser.parse(contents);
  //
  // const blob = new Blob([csv], {type: "application/vnd.ms-excel;charset=utf-8"});
  // saveAs(blob, `Team Today ${fileName}.xls`);
}


export interface KeyResponsibilitiesUsageReportItem {
  officeAttendance: number;
  date: string;
  count: number;
  officeCapacity: number;
  freeSeats?: number;
}

export function getKeyResponsibilitiesData(office: OfficeEntity, date: Moment, keyResponsibility: string): Promise<KeyResponsibilitiesUsageReportItem[]> {
  return agent.requests.get(`/api/report/key-responsibilities?officeId=${office.id}&responsibility=${keyResponsibility}&date=${date.format(DATE_FORMAT)}`);
}


interface ExportUserRow {
  firstName: string;
  lastName: string;
  email: string;
  team: string;
  location: string;
  permission: string;
  fireWarden: string;
  firstAider: string;
  keyHolder: string;
  mentalHealthFirstAider: string;
  lineReports: number;
  accountInUser: string;
}

export interface LineReportLine {
  id: number;
  managerUserId: string;
  managerFirstName: string;
  managerLastName: string;
  managerEmail: string;
  reporteeUserId: string;
  reporteeFirstName: string;
  reporteeLastName: string;
  reporteeEmail: string;
}

export async function exportAllUsersToCsv(downloadAsCsv: boolean): Promise<ExportUserRow> {
  const userList = await agent.requests.get('/api/user/export');

  if (downloadAsCsv) {
    toExcelDownload(userList,
      'team-today-users',
      ['firstName',
        'lastName',
        'email',
        'team',
        'location',
        'permission',
        'fireWarden',
        'firstAider',
        'keyHolder',
        'mentalHealthFirstAider',
        'lineReports',
        'accountInUse',
      ]);
  }
  return userList;
}
